import { useEffect, useState } from 'react';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';

/** Same choices as International Requests / RMA Requests / the Devices list. */
export const DUNNING_PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
/** 20 rows per page by default (requester, 2026-09-23) — both tables on the page. */
export const DUNNING_DEFAULT_PAGE_SIZE = 20;

/**
 * Client-side paging for the Dunning Review tables (requester, 2026-09-23).
 *
 * Every list on this page arrives WHOLE from the server and is small (a review queue, the
 * accounts switched off, the customers excluded in Stripe), so paging in the browser is the
 * honest shape — a server cursor would add an API change for a few dozen rows. `resetKey`
 * returns to the first page when what is being paged changes (a new search), so a narrowed
 * result never opens on an empty page 3.
 */
export function useClientPage<T>(rows: T[], resetKey: unknown = null) {
  const [pageSize, setPageSize] = useState<number>(DUNNING_DEFAULT_PAGE_SIZE);
  const [pageIndex, setPageIndex] = useState(0);
  useEffect(() => setPageIndex(0), [resetKey, pageSize]);

  const total = rows.length;
  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
  const index = Math.min(pageIndex, lastPage);
  const start = index * pageSize;
  return {
    pageRows: rows.slice(start, start + pageSize),
    total,
    pageIndex: index,
    pageSize,
    firstShown: total === 0 ? 0 : start + 1,
    lastShown: Math.min(start + pageSize, total),
    setPageSize,
    setPageIndex,
  };
}

type Page = ReturnType<typeof useClientPage>;

/**
 * "1–47 of 47 · Rows per page [50]" — the SAME control as International Requests (and RMA and
 * the Devices list): the count, then the `Rows per page` label said once, bare numbers in a
 * 72px h-8 trigger, `cursor-pointer` throughout, and an `aria-label` so the select is named.
 */
export function PagerSummary({ page, testId }: { page: Page; testId: string }) {
  return (
    <div className="flex shrink-0 items-center justify-end gap-3" data-testid={`${testId}-pager`}>
      <span className="text-xs text-muted-foreground" data-testid={`${testId}-count`}>
        {page.total === 0 ? 'No rows' : `${page.firstShown}–${page.lastShown} of ${page.total}`}
      </span>
      <span className="text-xs text-muted-foreground">{'Rows per page'}</span>
      <Select value={String(page.pageSize)} onValueChange={(v) => page.setPageSize(Number(v))}>
        <SelectTrigger
          className="h-8 w-[72px] cursor-pointer"
          aria-label={'Rows per page'}
          data-testid={`${testId}-page-size`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DUNNING_PAGE_SIZE_OPTIONS.map((n) => (
            <SelectItem key={n} value={String(n)} className="cursor-pointer">
              {n}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** "Page 2 of 3  [Previous] [Next]" under the table — only when there is more than one page. */
export function PagerNav({ page, testId }: { page: Page; testId: string }) {
  if (page.total <= page.pageSize) return null;
  const pages = Math.max(1, Math.ceil(page.total / page.pageSize));
  return (
    <div className="flex shrink-0 items-center justify-end gap-2 pt-1">
      <span className="text-xs text-muted-foreground" data-testid={`${testId}-page-label`}>
        {`Page ${page.pageIndex + 1} of ${pages}`}
      </span>
      <Button
        variant="outline"
        size="sm"
        disabled={page.pageIndex === 0}
        onClick={() => page.setPageIndex(page.pageIndex - 1)}
        data-testid={`${testId}-page-prev`}
      >
        {'Previous'}
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={page.lastShown >= page.total}
        onClick={() => page.setPageIndex(page.pageIndex + 1)}
        data-testid={`${testId}-page-next`}
      >
        {'Next'}
      </Button>
    </div>
  );
}
